import Constants from 'expo-constants';
import type { FC } from 'react';
import React from 'react';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import type { GroupTypeBase, OptionTypeBase, Styles } from 'react-select';
import styled from 'styled-components/native';

import OwnableTheme from '../assets/OwnableTheme';

type IPlacesInputProps = {
  onSelect: (address: any) => void;
  enabled?: boolean;
  styles?: Partial<
    Styles<OptionTypeBase, false, GroupTypeBase<OptionTypeBase>>
  >;
};

const Container = styled.View`
  z-index: 100;
  margin-bottom: 30px;
  border-bottom-width: 1px;
  border-bottom-color: ${OwnableTheme.colors.grayInput};
`;

const GooglePlacesInput: FC<IPlacesInputProps> = ({
  onSelect,
  enabled = true,
  styles,
}) => (
  <Container>
    <GooglePlacesAutocomplete
      autocompletionRequest={{
        componentRestrictions: {
          country: ['uk'],
        },
      }}
      apiKey={Constants.manifest?.extra?.googlePlacesApi}
      selectProps={{
        onChange: (e) => {
          if (enabled) {
            onSelect(e?.value.place_id);
          }
        },
        isDisabled: !enabled,
        isSearchable: enabled,
        className: 'react-select-container',
        classNamePrefix: 'react-select',
        placeholder: 'Start typing address',
        styles: styles ?? {
          placeholder: () => ({
            color: OwnableTheme.colors.persianIndigo + 50,
            textAlign: 'left',
            fontFamily: OwnableTheme.fonts.gilroy.regular,
            fontSize: OwnableTheme.fontSizesNumerical.l,
            width: '100%',
            position: 'absolute',
          }),
          loadingMessage: () => ({
            fontFamily: OwnableTheme.fonts.gilroy.regular,
            fontSize: OwnableTheme.fontSizesNumerical.xs,
            color: OwnableTheme.colors.persianIndigo,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }),
          noOptionsMessage: () => ({
            fontFamily: OwnableTheme.fonts.gilroy.regular,
            fontSize: OwnableTheme.fontSizesNumerical.xs,
            color: OwnableTheme.colors.persianIndigo,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }),
          option: () => ({
            fontFamily: OwnableTheme.fonts.gilroy.regular,
            marginBottom: 3,
            paddingLeft: 5,
            color: OwnableTheme.colors.persianIndigo,
            fontSize: OwnableTheme.fontSizesNumerical.xs,
          }),
          input: () => ({
            fontFamily: OwnableTheme.fonts.gilroy.regular,
            color: OwnableTheme.colors.persianIndigo,
            borderRadius: 999,
            backgroundColor: OwnableTheme.colors.palePersianIndigo,
            fontSize: OwnableTheme.fontSizesNumerical.l,
            // Manually set the position so that it overlays the placeholder
            // Without this, the height of the box adjusts if an item is selected and then the user starts typing
            position: 'absolute',
            left: 6,
            top: 4,
          }),
          control: () => ({
            fontFamily: OwnableTheme.fonts.gilroy.regular,
            color: OwnableTheme.colors.persianIndigo,
            borderRadius: 999,
            backgroundColor: OwnableTheme.colors.palePersianIndigo,
            flexDirection: 'row',
            justifyContent: 'space-between',
            display: 'flex',
            textAlign: 'left',
            paddingTop: 7,
            paddingBottom: 7,
            paddingLeft: 13,
          }),
          container: () => ({
            fontFamily: OwnableTheme.fonts.gilroy.regular,
            color: OwnableTheme.colors.persianIndigo,
            borderRadius: 999,
            backgroundColor: OwnableTheme.colors.palePersianIndigo,
            paddingVertical: 10,
          }),
          singleValue: () => ({
            fontSize: OwnableTheme.fontSizesNumerical.l,
            color: OwnableTheme.colors.persianIndigo,
            opacity: 0.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }),
        },
      }}
    />
  </Container>
);

export default GooglePlacesInput;
